/**
 * React Query Hooks for Suppliers
 *
 * Provides data fetching and caching for supplier operations using TanStack Query.
 */
import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { supplierService, FrontendSupplier } from '../../services/api';
import { queryKeys, STALE_TIMES } from '../../lib/queryClient';
import { useAuth } from '../../contexts/AuthContext';

// Re-export the FrontendSupplier type for convenience
export type { FrontendSupplier };

/**
 * Hook to fetch all suppliers
 * @param options - Additional query options
 */
export function useSuppliers(
  options?: Omit<UseQueryOptions<FrontendSupplier[], Error>, 'queryKey' | 'queryFn'>
) {
  const { token } = useAuth();

  return useQuery({
    queryKey: queryKeys.suppliers.list(),
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      return supplierService.getAll(token);
    },
    enabled: !!token,
    staleTime: STALE_TIMES.STATIC,
    ...options,
  });
}

/**
 * Hook to fetch a single supplier by ID
 * @param supplierId - The supplier ID to fetch
 * @param options - Additional query options
 */
export function useSupplier(
  supplierId: string | undefined,
  options?: Omit<UseQueryOptions<FrontendSupplier, Error>, 'queryKey' | 'queryFn'>
) {
  const { token } = useAuth();

  return useQuery({
    queryKey: queryKeys.suppliers.detail(supplierId || ''),
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      if (!supplierId) throw new Error('Supplier ID required');
      return supplierService.getById(token, supplierId);
    },
    enabled: !!token && !!supplierId,
    staleTime: STALE_TIMES.STATIC,
    ...options,
  });
}

/**
 * Hook to create a new supplier
 */
export function useCreateSupplier() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (supplierData: FrontendSupplier) => {
      if (!token) throw new Error('No authentication token');
      return supplierService.create(token, supplierData);
    },
    onSuccess: () => {
      // Invalidate supplier list queries to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.list() });
    },
  });
}

/**
 * Hook to update an existing supplier
 */
export function useUpdateSupplier() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: FrontendSupplier }) => {
      if (!token) throw new Error('No authentication token');
      return supplierService.update(token, id, updates);
    },
    onSuccess: (_, { id }) => {
      // Invalidate both detail and list queries
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.list() });
    },
  });
}

/**
 * Hook to delete a supplier
 */
export function useDeleteSupplier() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (supplierId: string) => {
      if (!token) throw new Error('No authentication token');
      return supplierService.delete(token, supplierId);
    },
    onSuccess: (_, supplierId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.suppliers.detail(supplierId) });
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.list() });
    },
  });
}
